import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Check, Home, Layers, List } from 'react-feather';
import { RootState } from '../../../store/store';
import { HeaderType } from '../../../types/Header';
import Tooltip from '@mui/material/Tooltip';
import StudyProgress from './StudyProgress';
import { hapticsImpactLight } from '../../../utils/haptics';
import OraLogo from '../OraLogo';
import { examsSelectors } from '../../../slices/exams/examsSlice';
import { selectTodaySession } from '../../../slices/todaySession/todaySessionSlice';
import { selectStudyPhase, StudyPhase } from '../../../slices/study/studySlice';
import { selectAllBlocksSubmitted } from '../../../slices/blocks/blocksSlice';
import { useStudyPhaseSend } from '../../../studyPhase';
import { isLoadingPhase } from '../../../slices/study/studySlice';

interface HeaderProps {
  type: HeaderType;
}

const Header: React.FC<HeaderProps> = ({ type }) => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const todaySession = useSelector(selectTodaySession);
  const flashcards = useSelector((state: RootState) => state.flashcards);
  const studyPhase = useSelector(selectStudyPhase);
  const allBlocksSubmitted = useSelector(selectAllBlocksSubmitted);
  const { sendPhaseEvent } = useStudyPhaseSend();

  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);

  const navigate = useNavigate();

  const [previousNonLoadingPhase, setPreviousNonLoadingPhase] =
    useState<StudyPhase | null>(null);

  // Track the previous non-loading phase
  useEffect(() => {
    if (!isLoadingPhase(studyPhase)) {
      setPreviousNonLoadingPhase(studyPhase);
    }
  }, [studyPhase]);

  const renderFlashcardButton = () => {
    if (!primaryExam?.isUsingFlashcards) return null;
    if (!todaySession?.flashcardCount || todaySession?.isReview) return null;
    if (flashcards.new.length + flashcards.due.length === 0) {
      return (
        <Tooltip title="No flashcards remaining." enterTouchDelay={0}>
          <span>
            <button
              className="button button--phase"
              onClick={() => {
                hapticsImpactLight();
              }}
              disabled
            >
              <Check />
              <span>Flashcards</span>
            </button>
          </span>
        </Tooltip>
      );
    } else {
      return (
        <button
          className="button button--phase"
          onClick={() => {
            sendPhaseEvent({ type: 'START_FLASHCARDS' });
            localStorage.removeItem('ora-today-block-id');
            hapticsImpactLight();
          }}
        >
          <Layers />
          <span>Flashcards</span>
        </button>
      );
    }
  };

  const renderOraLogo = () => {
    if (type === 'study') return;
    if (currentUser && currentUser.id) {
      return <OraLogo />;
    } else {
      return <OraLogo type="link" url={'https://www.oraai.com'} />;
    }
  };

  const shouldHideHeader = () => {
    if (type !== 'study') return false;

    if (isLoadingPhase(studyPhase)) {
      // During loading phase, use the previous non-loading phase to determine visibility
      // This is to prevent the header from flashing during transitions
      return (
        previousNonLoadingPhase === StudyPhase.BLOCK_REVIEW ||
        previousNonLoadingPhase === StudyPhase.BLOCK_STUDY
      );
    }

    // For non-loading phases, use the current phase
    return (
      studyPhase === StudyPhase.BLOCK_REVIEW ||
      studyPhase === StudyPhase.BLOCK_STUDY
    );
  };

  if (shouldHideHeader()) {
    return;
  }

  return (
    <div
      className={`header ${type ? 'header--' + type : ''}${
        !primaryExam?.isUsingQbank ? ' header--study--flashcard-only' : ''
      }`}
    >
      <Tooltip
        title={
          type === 'study' &&
          (studyPhase === StudyPhase.BLOCK_STUDY ||
            studyPhase === StudyPhase.FLASHCARD_STUDY)
            ? 'Your progress will be saved.'
            : ''
        }
        enterTouchDelay={0}
      >
        <button
          role="link"
          className={type === 'study' ? 'button button--phase' : 'button--ora'}
          style={type === 'generic' ? { cursor: 'default' } : {}}
          onClick={() => {
            if (type !== 'generic') {
              if (type === 'study') {
                sendPhaseEvent({ type: 'RETURN_TO_HOME' });
              } else {
                navigate('/');
              }
              hapticsImpactLight();
            }
          }}
        >
          {renderOraLogo()}
          {type === 'study' &&
            (studyPhase === StudyPhase.FLASHCARD_STUDY ||
              studyPhase === StudyPhase.BLOCK_STUDY ||
              studyPhase === StudyPhase.BLOCK_SELECT ||
              studyPhase === StudyPhase.VIDEO_STUDY ||
              studyPhase === StudyPhase.SESSION_SUMMARY) && (
              <>
                <Home />
                <span>Home</span>
              </>
            )}
        </button>
      </Tooltip>
      {type === 'study' && (
        <div className="study-middle">
          {studyPhase === StudyPhase.FLASHCARD_STUDY && <StudyProgress />}
        </div>
      )}
      {type === 'study' && (
        <>
          {studyPhase === StudyPhase.FLASHCARD_STUDY &&
          todaySession?.isUsingQbank &&
          todaySession?.vignetteCount > 0 ? (
            <button
              className="button button--phase"
              onClick={() => {
                sendPhaseEvent({ type: 'START_QBANK' });
                hapticsImpactLight();
              }}
            >
              {allBlocksSubmitted ? <Check /> : <List />}
              <span>QBank</span>
            </button>
          ) : (
            <div className="spacer-button"></div>
          )}
          {studyPhase === StudyPhase.BLOCK_SELECT && renderFlashcardButton()}
        </>
      )}
    </div>
  );
};

export default Header;
